import jsPDF from 'jspdf';

/**
 * Hook personalizado para manejar la exportación a PDF
 */
export const usePDFExport = () => {
  /**
   * Exporta datos a un archivo PDF simple
   * @param {Object} options - Configuración del PDF
   * @param {string} options.title - Título del reporte
   * @param {Array} options.headers - Array de encabezados
   * @param {Array} options.data - Array de arrays con los datos
   * @param {string} options.filename - Nombre del archivo
   * @param {string} options.orientation - 'portrait' o 'landscape'
   */
  const exportToPDF = ({ title, headers, data, filename, orientation = 'portrait' }) => {
    try {
      const doc = new jsPDF(orientation);
      let yOffset = 20;

      doc.setFontSize(12);
      doc.text(title, orientation === 'landscape' ? 148 : 105, yOffset, { align: 'center' });
      yOffset += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');

      const columnWidth = orientation === 'landscape' ? 28 : 30;
      let startX = 10;

      headers.forEach((header, index) => {
        doc.text(header, startX + index * columnWidth, yOffset);
      });
      yOffset += 8;

      doc.setFont('helvetica', 'normal');

      data.forEach((row) => {
        startX = 10;

        row.forEach((cell, index) => {
          const text = cell?.toString() || '';

          const truncatedText = text.length > 10 ? text.substring(0, 10) + '...' : text;

          doc.text(truncatedText, startX + index * columnWidth, yOffset);
        });

        yOffset += 8;

        const maxY = orientation === 'landscape' ? 190 : 270;
        if (yOffset > maxY) {
          doc.addPage();
          yOffset = 20;

          doc.setFont('helvetica', 'bold');
          headers.forEach((header, index) => {
            doc.text(header, 10 + index * columnWidth, yOffset);
          });
          yOffset += 8;
          doc.setFont('helvetica', 'normal');
        }
      });

      doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw new Error('Error al generar el archivo PDF');
    }
  };

  /**
   * Exporta un PDF complejo con múltiples secciones
   * @param {Object} options - Configuración del PDF complejo
   */
  const exportComplexToPDF = (options) => {
    try {
      const doc = new jsPDF(options.orientation || 'landscape');
      let yOffset = 10;
      const pageWidth = doc.internal.pageSize.width;

      doc.setFontSize(14);
      doc.text(options.title, pageWidth / 2, yOffset, { align: 'center' });
      yOffset += 10;

      options.sections.forEach((section) => {
        if (yOffset > 180) {
          doc.addPage();
          yOffset = 10;
        }

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(section.title, 10, yOffset);
        yOffset += 6;

        if (section.subtitle) {
          doc.text(section.subtitle, 10, yOffset);
          yOffset += 6;
        }

        doc.setFontSize(9);
        section.data.forEach((item) => {
          doc.text(item, 10, yOffset);
          yOffset += 6;
        });

        yOffset += 5;
      });

      doc.save(`${options.filename}.pdf`);
    } catch (error) {
      console.error('Error exporting complex PDF:', error);
      throw new Error('Error al generar el archivo PDF complejo');
    }
  };

  /**
   * Exporta datos con muchas columnas en formato de bloques
   * @param {Object} options - Configuración del PDF
   * @param {string} options.title - Título del reporte
   * @param {Array} options.headers - Array de encabezados
   * @param {Array} options.data - Array de arrays con los datos
   * @param {string} options.filename - Nombre del archivo
   * @param {number} options.columnsPerRow - Número de columnas por fila (default: 3)
   * @param {(row: string[], index: number) => string} [options.recordTitleResolver] - Genera el título de cada bloque
  */
  const exportBlockFormatToPDF = ({
    title,
    headers,
    data,
    filename,
    columnsPerRow = 3,
    rightTopText,
    recordTitleResolver,
  }) => {
    try {
      const doc = new jsPDF('landscape');
      const margin = 15;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const blockWidth = (pageWidth - margin * 2 - (columnsPerRow - 1) * 10) / columnsPerRow;
      const lineHeight = 14;
      const headerHeight = 10;
      const blockPadding = 10;
      let yOffset = 20;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, pageWidth / 2, yOffset, { align: 'center' });
      if (rightTopText) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(rightTopText, pageWidth - margin, yOffset, { align: 'right' });
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
      }
      yOffset += 15;

      data.forEach((row, rowIndex) => {
        const totalRows = Math.ceil(headers.length / columnsPerRow);
        const blockHeight = totalRows * lineHeight + blockPadding;

        if (yOffset + blockHeight > pageHeight - 20) {
          doc.addPage();
          yOffset = 20;
        }

        const blockStartY = yOffset - 5;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        const recordTitle =
          typeof recordTitleResolver === 'function'
            ? recordTitleResolver(row, rowIndex)
            : `Registro ${rowIndex + 1}`;
        doc.text(recordTitle, margin + 5, yOffset);
        yOffset += headerHeight;

        let currentColumn = 0;
        let currentRow = 0;
        const startY = yOffset;

        doc.setFontSize(11);

        headers.forEach((header, index) => {
          const value = row[index]?.toString() || '-';

          const xPos = margin + 5 + currentColumn * (blockWidth + 5);
          const yPos = startY + currentRow * lineHeight;

          doc.setFont('helvetica', 'bold');
          doc.text(`${header}:`, xPos, yPos);

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          const maxValueLength = blockWidth - Math.floor(blockWidth / 3.5);
          const truncatedValue =
            value.length > maxValueLength ? value.substring(0, maxValueLength - 3) + '...' : value;
          doc.text(truncatedValue, xPos, yPos + 5);

          currentColumn++;
          if (currentColumn >= columnsPerRow) {
            currentColumn = 0;
            currentRow++;
          }
        });

        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.5);
        doc.rect(margin, blockStartY, pageWidth - margin * 2, blockHeight);

        yOffset = blockStartY + blockHeight + 5;
      });

      doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error exporting block format PDF:', error);
      throw new Error('Error al generar el archivo PDF en formato de bloques');
    }
  };

  return {
    exportToPDF,
    exportComplexToPDF,
    exportBlockFormatToPDF,
  };
};
